import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Printer,
  Tags,
  ClipboardList,
  ScanLine,
  Plug,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  Loader2,
  CheckCircle2,
  X,
  Monitor,
  Save,
  Search,
  AlertCircle,
  AlertTriangle,
  Cloud,
  Link2,
  Server,
  ScrollText,
  Receipt,
  Eye,
  EyeOff,
} from 'lucide-react';
import { settingsService, DetectedPrinter, DevicePayload, PrintAgentInfo, PrintJobLog } from '../../services/httpServices/settingsService';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
  getDevices,
  saveDevices,
  makeDevice,
  getDeviceHost,
  isRemoteHost,
  PRINTER_DEVICE_TYPES,
  DeviceType,
  ConnectedDevice,
} from '../../utils/devices';
import { savePrinterShareName } from '../../utils/printerShare';
import { agentDetectPrinters, getPrintAgentIdentities, agentSyncPrinters, agentSyncAllowedOrigins, isPrintAgentAvailable, agentCheckShare, agentSharePrinter, agentResolveHost, probePrintAgent, getPrintAgentStatus, subscribePrintAgentStatus, refreshPrintAgentStatus, type PrintAgentStatus } from '../../utils/printAgent';
import { publishLocalPrinters, parseUncPort } from '../../utils/devicePublish';
import {
  buildRemotePrinters,
  getCentralAllowedOrigins,
  invalidateCentralAllowedOrigins,
  ensureCurrentOrigin,
} from '../../utils/remotePrinters';
import { isPrinterOnline } from '../../utils/printPresence';

interface DeviceTypeMeta {
  value: DeviceType;
  label: string;
  icon: React.ElementType;
  usesShare: boolean;
  usesPort: boolean;
}

const DEVICE_TYPES: DeviceTypeMeta[] = [
  { value: 'thermal_printer', label: 'Thermal Printer (Receipt)', icon: Printer, usesShare: true, usesPort: false },
  { value: 'label_printer', label: 'Label Printer', icon: Tags, usesShare: true, usesPort: false },
  { value: 'printer', label: 'Regular Printer', icon: Printer, usesShare: true, usesPort: false },
  { value: 'qr_scanner', label: 'QR / Barcode Scanner', icon: ScanLine, usesShare: false, usesPort: true },
  { value: 'other', label: 'Other Device', icon: Plug, usesShare: false, usesPort: true },
];

const TYPE_META: Record<DeviceType, DeviceTypeMeta> = Object.fromEntries(
  DEVICE_TYPES.map((t) => [t.value, t]),
) as Record<DeviceType, DeviceTypeMeta>;

// Dismissal flag for the "can't reach the print agent" hint, kept per browser.
const AGENT_HINT_DISMISSED_KEY = 'print_agent_reachability_hint_dismissed';

function readAgentHintDismissed(): boolean {
  try {
    return localStorage.getItem(AGENT_HINT_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeAgentHintDismissed(): void {
  try {
    localStorage.setItem(AGENT_HINT_DISMISSED_KEY, '1');
  } catch {
    /* ignore */
  }
}

/** True for an IPv4 literal (e.g. 192.168.1.50). */
function isIpv4(value: string): boolean {
  return /^\d{1,3}(\.\d{1,3}){3}$/.test((value || '').trim());
}

/** True when `host` refers to the PC the dashboard is running on. */
function isLocalPrinterHost(host: string, localHostname: string): boolean {
  const h = (host || '').trim().toLowerCase();
  if (!h || h === 'localhost' || h === '127.0.0.1' || h === '.') return true;
  return Boolean(localHostname) && h === localHostname.trim().toLowerCase();
}

/**
 * Work out a detected printer's share name and, when it is a printer installed
 * from another PC, the host to target. A network printer added in Windows
 * exposes the remote UNC in `portName` (e.g. `\\DESKTOP-20REHG3\RONGTA80`) and
 * usually has an empty *local* share name, so the port is the only place the
 * remote share can be read from — this is what makes detection auto-fill the
 * PC name instead of asking the user to type it.
 */
function resolveDetectedPrinter(
  printer: DetectedPrinter,
  localHostname: string,
): { shareName: string; remoteHost: string } {
  const unc = parseUncPort(printer.portName);
  const remoteHost = unc && !isLocalPrinterHost(unc.host, localHostname) ? unc.host : '';
  return { shareName: unc?.share || printer.shareName || '', remoteHost };
}

/** Suggest a Windows share name for a printer (safe charset, no spaces). */
function suggestShareName(printerName: string): string {
  const cleaned = (printerName || '')
    .replace(/[^A-Za-z0-9 _.-]/g, '')
    .replace(/\s+/g, '')
    .trim();
  return (cleaned || 'FastAidPrinter').slice(0, 128);
}

/** Badge tone for a print-job status. */
function logStatusVariant(
  status: string,
): 'success' | 'danger' | 'warning' | 'primary' | 'default' {
  switch ((status || '').toLowerCase()) {
    case 'done':
      return 'success';
    case 'failed':
      return 'danger';
    case 'pending':
      return 'warning';
    case 'delivered':
    case 'printing':
      return 'primary';
    default:
      return 'default';
  }
}

/** Human-readable byte size. */
function formatBytes(bytes?: number): string {
  if (!bytes || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Compact local date-time for log rows. */
function formatLogTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

interface FormState {
  name: string;
  type: DeviceType;
  shareName: string;
  port: string;
  pcName: string;
  networkIp: string;
  enabled: boolean;
  isReceiptPrinter: boolean;
  isLabelPrinter: boolean;
  isDiagnosticReceiptPrinter: boolean;
  isInvoicePrinter: boolean;
  isDocumentPrinter: boolean;
}

const emptyForm: FormState = {
  name: '',
  type: 'printer',
  shareName: '',
  port: '',
  pcName: '',
  networkIp: '',
  enabled: true,
  isReceiptPrinter: false,
  isLabelPrinter: false,
  isDiagnosticReceiptPrinter: false,
  isInvoicePrinter: false,
  isDocumentPrinter: false,
};

export function DevicesSettings() {
  const [devices, setDevices] = useState<ConnectedDevice[]>(() => getDevices());
  // Latest devices, so effects that run after an async registry load can read
  // the current list without re-subscribing.
  const devicesRef = useRef(devices);
  useEffect(() => {
    devicesRef.current = devices;
  }, [devices]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  const [printers, setPrinters] = useState<DetectedPrinter[] | null>(null);
  const [detecting, setDetecting] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Name of this workstation (from the local print agent), used to tag devices
  // in the global registry so other PCs can target them.
  const [localHostname, setLocalHostname] = useState('');
  // IPv4 addresses of this workstation (from the local print agent), so a device
  // whose host is this PC's IP is still recognised as local.
  const [localIps, setLocalIps] = useState<string[]>([]);
  // Global registry of devices registered by any workstation.
  const [registry, setRegistry] = useState<DevicePayload[] | null>(null);
  // Global registry snapshot used to build the agent's remote-printer list.
  const [registryDevices, setRegistryDevices] = useState<DevicePayload[]>([]);
  const [registryOpen, setRegistryOpen] = useState(false);
  const [loadingRegistry, setLoadingRegistry] = useState(false);
  // Per-device reachability of the printer share, keyed by device id.
  const [reachability, setReachability] = useState<
    Record<string, { state: 'checking' | 'ok' | 'fail'; message?: string }>
  >({});
  // Sharing a not-yet-shared local printer through its agent.
  const [shareTarget, setShareTarget] = useState<DetectedPrinter | null>(null);
  const [shareNameInput, setShareNameInput] = useState('');
  const [sharing, setSharing] = useState(false);
  // Re-resolving the host (PC name / IP) from the Add/Edit Device dialog.
  const [resolvingHost, setResolvingHost] = useState(false);
  // Host identities whose dashboard is currently connected to receive prints
  // (from the backend print gateway), so we can show whether a printer's PC is
  // online right now.
  const [onlineHosts, setOnlineHosts] = useState<string[]>([]);
  // Enrolled print agents (socket clients) and their live status.
  const [printAgents, setPrintAgents] = useState<PrintAgentInfo[]>([]);
  // Whether revoked agents are shown in the agents list (hidden by default).
  const [showRevokedAgents, setShowRevokedAgents] = useState(false);
  // Revoked agent awaiting permanent removal, and the in-flight flag.
  const [removeAgentTarget, setRemoveAgentTarget] = useState<PrintAgentInfo | null>(null);
  const [removingAgent, setRemovingAgent] = useState(false);
  // Centrally-managed dashboard-origin allow-list pushed to each PC's agent.
  const [originsText, setOriginsText] = useState('');
  const [originsLoaded, setOriginsLoaded] = useState(false);
  const [savingOrigins, setSavingOrigins] = useState(false);
  // Live local-agent reachability (drives the "can't reach the agent" hint).
  const [agentStatus, setAgentStatus] = useState<PrintAgentStatus>(() => getPrintAgentStatus());
  const [agentHintDismissed, setAgentHintDismissed] = useState(() => readAgentHintDismissed());
  const [agentRetrying, setAgentRetrying] = useState(false);

  // ── Print activity log (the "Print log" tab) ──────────────────────────────
  const [printLogs, setPrintLogs] = useState<PrintJobLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPageSize, setLogsPageSize] = useState(25);
  const [logStatus, setLogStatus] = useState('');
  const [logSource, setLogSource] = useState('');
  const [logSearch, setLogSearch] = useState('');
  // Row selection for bulk delete.
  const [selectedLogIds, setSelectedLogIds] = useState<Set<string>>(new Set());
  // Auto-delete retention (days; 0 = keep forever) and its in-progress draft.
  const [retentionDays, setRetentionDays] = useState(0);
  const [retentionDraft, setRetentionDraft] = useState(0);
  const [savingRetention, setSavingRetention] = useState(false);
  // Destructive-action confirmations and their in-flight state.
  const [clearLogsOpen, setClearLogsOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deletingLogs, setDeletingLogs] = useState(false);
  // Id of the log row currently being re-sent (shows a spinner on its button).
  const [reprintingId, setReprintingId] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    getPrintAgentIdentities()
      .then((id) => {
        if (!mounted) return;
        if (id.hostname) setLocalHostname(id.hostname);
        if (id.ipAddresses.length) setLocalIps(id.ipAddresses);
      })
      .catch(() => {
        /* no agent — leave blank */
      });
    settingsService
      .listDevices()
      .then((res) => {
        if (mounted) setRegistryDevices(res.data || []);
      })
      .catch(() => {
        /* registry unavailable — ignore */
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Mirror the print agent's reachability into the UI. The subscription fires
  // immediately with the current status and on every change, so the hint below
  // clears by itself once the agent answers again.
  useEffect(() => subscribePrintAgentStatus(setAgentStatus), []);

  // Load the central print-agent allow-list so it can be edited here.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getCentralAllowedOrigins();
        if (cancelled) return;
        const initial = list.length
          ? list
          : ensureCurrentOrigin([
              'https://admin.fastaid.com.bd',
              'http://localhost:5173',
              'http://localhost:5174',
              'http://localhost:4173',
            ]);
        setOriginsText(initial.join('\n'));
      } finally {
        if (!cancelled) setOriginsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Self-heal missing printer hosts from the global registry. A printer that the
  // agent registered (workstation only, no PC name/IP) or one added before host
  // capture would otherwise look local and show a bogus "Not reachable", and its
  // jobs would never reach the print agent on the owning PC. Matching by share
  // name lets the owning PC's hostname/IP be filled in automatically.
  useEffect(() => {
    if (registryDevices.length === 0) return;
    const current = devicesRef.current;
    let changed = false;
    const next = current.map((d) => {
      if (!PRINTER_DEVICE_TYPES.includes(d.type) || !d.shareName || getDeviceHost(d)) return d;
      const match = registryDevices.find(
        (r) =>
          (r.shareName || '').trim().toLowerCase() === d.shareName.trim().toLowerCase() &&
          Boolean((r.networkIp || r.pcName || r.workstation || '').trim()),
      );
      if (!match) return d;
      changed = true;
      return {
        ...d,
        pcName: (match.pcName || match.workstation || '').trim(),
        networkIp: (match.networkIp || '').trim(),
        workstation: (match.workstation || '').trim(),
      };
    });
    if (!changed) return;
    persist(next);
    next.forEach((d, i) => {
      if (d !== current[i]) syncDevice(d);
    });
    // Intentionally keyed only on the registry snapshot: `syncDevice`/`persist`
    // are recreated each render and must not re-trigger this heal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryDevices]);

  // Keep the local agent's per-printer "Print to another PC" list in sync with
  // the printers configured here (and in the global registry).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!(await isPrintAgentAvailable())) return;
      const printers = buildRemotePrinters(localHostname, devices, registryDevices, localIps);
      if (cancelled) return;
      try {
        await agentSyncPrinters(printers);
      } catch {
        /* agent offline / older version — ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [devices, registryDevices, localHostname, localIps]);

  // Auto-publish this PC's shared printers to the global registry so other
  // workstations can discover them with the host auto-filled. Also runs at app
  // start (see MainLayout); kept here so the list refreshes after Detect.
  useEffect(() => {
    if (!localHostname) return;
    void publishLocalPrinters();
  }, [localHostname]);

  // Track which printer PCs are currently connected (a dashboard receiving
  // prints) so the device list and registry can show online/offline and a host
  // mismatch becomes obvious.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await settingsService.listPrintClients();
        if (!cancelled) setOnlineHosts(res.data?.identities || []);
      } catch {
        /* backend/print gateway unavailable — leave the last known state */
      }
      try {
        const res = await settingsService.listPrintAgents();
        if (!cancelled) setPrintAgents(res.data?.agents || []);
      } catch {
        /* print agents unavailable — leave the last known state */
      }
    };
    void load();
    const timer = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  /**
   * Whether a printer on `host` (optionally identified by its `shareName`) is
   * reachable right now. Thin wrapper over the shared presence helper so the
   * device list and the "Printers on other PCs" modal can never disagree.
   */
  const isHostOnline = (host: string, shareName?: string): boolean =>
    isPrinterOnline(host, shareName, printAgents, onlineHosts);

  const revokeAgent = async (agent: PrintAgentInfo) => {
    try {
      await settingsService.revokePrintAgent(agent.id);
      toast.success(`Revoked agent ${agent.hostname || agent.id}`);
      setPrintAgents((prev) =>
        prev.map((a) => (a.id === agent.id ? { ...a, enabled: false, online: false } : a)),
      );
    } catch (err: any) {
      toast.error(err?.message || 'Could not revoke the agent');
    }
  };

  /**
   * Permanently remove a revoked agent from the list. This only deletes the
   * record — the PC can still enrol again later and will show up as a new agent.
   */
  const removeAgent = async () => {
    if (!removeAgentTarget) return;
    setRemovingAgent(true);
    try {
      await settingsService.removePrintAgent(removeAgentTarget.id);
      toast.success(`Removed agent ${removeAgentTarget.hostname || removeAgentTarget.id}`);
      setPrintAgents((prev) => prev.filter((a) => a.id !== removeAgentTarget.id));
      setRemoveAgentTarget(null);
    } catch (err: any) {
      toast.error(err?.message || 'Could not remove the agent');
    } finally {
      setRemovingAgent(false);
    }
  };

  /** Check whether a printer's share is reachable from this PC via the agent. */
  const checkReachability = async (device: ConnectedDevice) => {
    if (!device.shareName) return;
    const host = getDeviceHost(device);
    setReachability((prev) => ({ ...prev, [device.id]: { state: 'checking' } }));
    try {
      // Distinguish "no agent listening on this PC" from "agent answered but
      // rejected the call" (token/origin) so the badge shows the real reason.
      let health;
      try {
        health = await probePrintAgent(1200, true);
      } catch (err: any) {
        setReachability((prev) => ({
          ...prev,
          [device.id]: { state: 'fail', message: err?.message || 'The print agent rejected the request.' },
        }));
        return;
      }
      if (!health) {
        setReachability((prev) => ({
          ...prev,
          [device.id]: { state: 'fail', message: 'No print agent is running on this PC.' },
        }));
        return;
      }
      const res = await agentCheckShare(device.shareName, host || undefined);
      setReachability((prev) => ({
        ...prev,
        [device.id]: res.reachable
          ? { state: 'ok' }
          : {
              state: 'fail',
              message:
                res.error || `Could not reach \\${host || 'localhost'}\\${device.shareName}`,
            },
      }));
    } catch (err: any) {
      setReachability((prev) => ({
        ...prev,
        [device.id]: { state: 'fail', message: err?.message || 'Reachability check failed' },
      }));
    }
  };

  const renderReachability = (device: ConnectedDevice) => {
    const r = reachability[device.id];
    if (!r) {
      return (
        <button
          onClick={() => checkReachability(device)}
          className="text-xs text-primary hover:underline"
        >
          Check reachability
        </button>
      );
    }
    if (r.state === 'checking') {
      return (
        <span className="text-xs text-text-muted inline-flex items-center gap-1">
          <Loader2 size={12} className="animate-spin" /> Checking…
        </span>
      );
    }
    if (r.state === 'ok') {
      return (
        <button
          onClick={() => checkReachability(device)}
          className="text-xs text-success inline-flex items-center gap-1 hover:underline"
          title="Reachable from this PC"
        >
          <CheckCircle2 size={12} /> Reachable
        </button>
      );
    }
    return (
      <button
        onClick={() => checkReachability(device)}
        className="text-xs text-danger inline-flex items-center gap-1 hover:underline"
        title={r.message || 'Not reachable'}
      >
        <AlertCircle size={12} /> Not reachable
      </button>
    );
  };

  // Auto-verify (once) that each configured *local* printer share is reachable
  // from this PC. Printers on other PCs print through the cloud relay, so an SMB
  // check from here is not meaningful for them.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!(await isPrintAgentAvailable())) return;
      const identity = await getPrintAgentIdentities();
      const targets = getDevices().filter(
        (d) =>
          d.enabled &&
          d.shareName &&
          PRINTER_DEVICE_TYPES.includes(d.type) &&
          !isRemoteHost(getDeviceHost(d), identity.hostname, identity.ipAddresses),
      );
      for (const device of targets) {
        if (cancelled) return;
        await checkReachability(device);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSaveOrigins = async () => {
    const parsed = originsText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    const origins = ensureCurrentOrigin(parsed);
    if (origins.length === 0) {
      toast.error('Add at least one dashboard URL.');
      return;
    }
    setSavingOrigins(true);
    try {
      const res = await settingsService.setAllowedAgentOrigins(origins);
      const saved = res.data?.origins?.length ? res.data.origins : origins;
      invalidateCentralAllowedOrigins();
      setOriginsText(saved.join('\n'));
      // Apply on this PC immediately; other PCs pick it up on their next sync.
      try {
        await agentSyncAllowedOrigins(saved);
        toast.success("Allowed URLs saved and pushed to this PC's print agent.");
      } catch (err: any) {
        toast.warning(
          `Saved, but this PC's print agent did not accept them (${
            err?.message || 'agent offline'
          }). Other PCs get them on their next sync.`,
        );
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not save the allowed URLs.');
    } finally {
      setSavingOrigins(false);
    }
  };

  // Retry the local agent after the user grants local-network access (or starts
  // it). Bypasses the probe backoff so the result is immediate.
  const retryPrintAgent = async () => {
    setAgentRetrying(true);
    try {
      const health = await refreshPrintAgentStatus();
      if (health) toast.success('Print agent reachable again.');
      else
        toast.error(
          "Still can't reach the print agent. Allow local network access for this site, then retry.",
        );
    } finally {
      setAgentRetrying(false);
    }
  };

  const dismissAgentHint = () => {
    writeAgentHintDismissed();
    setAgentHintDismissed(true);
  };

  const persist = (next: ConnectedDevice[]) => {
    setDevices(next);
    saveDevices(next);
    const receipt = next.find((d) => d.isReceiptPrinter && d.enabled);
    savePrinterShareName(receipt?.shareName || '');
  };

  // Mirror a device to the database (fire-and-forget — localStorage remains the
  // per-workstation source of truth, the DB is the global registry). Devices
  // registered on this PC are tagged with its hostname so other workstations can
  // pick them and print to them as \\<workstation>\<shareName>.
  const syncDevice = (device: ConnectedDevice) => {
    settingsService
      .upsertDevice({
        clientId: device.id,
        name: device.name,
        type: device.type,
        shareName: device.shareName,
        port: device.port,
        pcName: device.pcName,
        networkIp: device.networkIp,
        workstation: device.workstation || localHostname || undefined,
        enabled: device.enabled,
        isReceiptPrinter: device.isReceiptPrinter,
        isLabelPrinter: device.isLabelPrinter,
        isDiagnosticReceiptPrinter: device.isDiagnosticReceiptPrinter,
        isInvoicePrinter: device.isInvoicePrinter,
        isDocumentPrinter: device.isDocumentPrinter,
      })
      .catch(() => {
        /* offline / backend unavailable — localStorage copy is still saved */
      });
  };

  const syncDelete = (clientId: string) => {
    settingsService.deleteDevice(clientId).catch(() => {
      /* ignore sync failures */
    });
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (device: ConnectedDevice) => {
    setEditingId(device.id);
    setForm({
      name: device.name,
      type: device.type,
      shareName: device.shareName,
      port: device.port,
      pcName: device.pcName,
      networkIp: device.networkIp,
      enabled: device.enabled,
      isReceiptPrinter: device.isReceiptPrinter,
      isLabelPrinter: device.isLabelPrinter,
      isDiagnosticReceiptPrinter: device.isDiagnosticReceiptPrinter,
      isInvoicePrinter: device.isInvoicePrinter,
      isDocumentPrinter: device.isDocumentPrinter,
    });
    setFormOpen(true);
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveForm = async () => {
    if (!form.name.trim()) {
      toast.error('Give the device a name');
      return;
    }
    const meta = TYPE_META[form.type];
    if (meta.usesShare && !form.shareName.trim()) {
      toast.error('Enter a share name for this printer');
      return;
    }

    const existing = editingId ? devices.find((d) => d.id === editingId) : undefined;
    // A local shared printer must carry this PC's hostname so other PCs can
    // target it through the global registry. Fall back to a fresh agent probe
    // when the state has not loaded yet (e.g. the agent started after this page).
    let workstation = existing?.workstation || localHostname;
    if (
      !workstation &&
      meta.usesShare &&
      form.shareName.trim() &&
      !form.pcName.trim() &&
      !form.networkIp.trim()
    ) {
      workstation = (await getPrintAgentIdentities()).hostname;
    }
    const saved = makeDevice({
      ...form,
      id: editingId || undefined,
      // Preserve where the device came from (relevant for printers picked from
      // another PC); new local devices are tagged with this PC's hostname.
      workstation,
    });
    let next: ConnectedDevice[];
    if (editingId) {
      next = devices.map((d) => (d.id === editingId ? saved : d));
    } else {
      next = [...devices, saved];
    }
    // Enforce a single receipt, label and document printer.
    if (saved.isReceiptPrinter) {
      next = next.map((d) => (d.id === saved.id ? saved : { ...d, isReceiptPrinter: false }));
    }
    if (saved.isLabelPrinter) {
      next = next.map((d) => (d.id === saved.id ? saved : { ...d, isLabelPrinter: false }));
    }
    if (saved.isDiagnosticReceiptPrinter) {
      next = next.map((d) =>
        d.id === saved.id ? saved : { ...d, isDiagnosticReceiptPrinter: false },
      );
    }
    if (saved.isInvoicePrinter) {
      next = next.map((d) => (d.id === saved.id ? saved : { ...d, isInvoicePrinter: false }));
    }
    if (saved.isDocumentPrinter) {
      next = next.map((d) => (d.id === saved.id ? saved : { ...d, isDocumentPrinter: false }));
    }
    persist(next);
    syncDevice(saved);
    setFormOpen(false);
    toast.success(editingId ? 'Device updated' : 'Device added');
  };

  const handleToggleEnabled = (device: ConnectedDevice) => {
    const next = devices.map((d) =>
      d.id === device.id ? { ...d, enabled: !d.enabled } : d,
    );
    persist(next);
    syncDevice({ ...device, enabled: !device.enabled });
  };

  const handleDelete = () => {
    if (!deleteId) return;
    persist(devices.filter((d) => d.id !== deleteId));
    syncDelete(deleteId);
    setDeleteId(null);
    toast.success('Device removed');
  };

  const handleDetect = async () => {
    setDetecting(true);
    setPrinters(null);
    try {
      // Detect on THIS computer through the local print agent (installed on the
      // workstation). The cloud backend cannot see the user's local printers.
      const local = await agentDetectPrinters(5000, true);
      if (local) {
        setPrinters(local);
        if (local.length === 0) {
          toast.info('No printers detected on this computer. Add a device manually instead.');
        }
        return;
      }

      // No agent running — fall back to the backend endpoint. This only works
      // when the backend runs on-prem (same machine/LAN as the printers).
      const res = await settingsService.detectPrinters();
      setPrinters(res.data || []);
      if (!res.data || res.data.length === 0) {
        toast.info(
          'No printers detected. Install the local print agent on this PC, or add a device manually.',
        );
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to detect printers');
    } finally {
      setDetecting(false);
    }
  };

  /**
   * Fill the PC name / network IP for a host using the local print agent's DNS
   * (a browser cannot resolve names or IPs). Best effort: when the agent is not
   * reachable or cannot resolve the host, falls back to what we already know.
   */
  const resolveHostFields = async (
    host: string,
    fallbackPcName = '',
  ): Promise<{ pcName: string; networkIp: string }> => {
    const h = (host || '').trim();
    if (!h) return { pcName: fallbackPcName, networkIp: '' };

    if (await isPrintAgentAvailable(1200, true)) {
      const res = await agentResolveHost(h);
      if (res && res.ok && !res.error) {
        const ip = (res.ipAddresses || []).find((a) => isIpv4(a)) || '';
        const name = (res.pcName || '').trim();
        if (isIpv4(h)) return { pcName: name || fallbackPcName, networkIp: h };
        return { pcName: name || h, networkIp: ip };
      }
    }

    // No agent / resolution failed — keep whatever the host literal tells us.
    return isIpv4(h)
      ? { pcName: fallbackPcName, networkIp: h }
      : { pcName: h, networkIp: '' };
  };

  /**
   * Re-resolve the host currently in the Add/Edit dialog (from its PC name or
   * network IP) so both fields pick up a changed machine name or address.
   */
  const refreshHost = async () => {
    const host = (form.pcName || form.networkIp || localHostname).trim();
    if (!host) {
      toast.error('Enter a PC name or network IP first');
      return;
    }
    setResolvingHost(true);
    try {
      if (!(await isPrintAgentAvailable(1200, true))) {
        toast.error('No print agent is running on this PC, so the host cannot be resolved.');
        return;
      }
      const res = await agentResolveHost(host);
      const ips = (res?.ipAddresses || []).filter((a) => isIpv4(a));
      if (!res || !res.ok || res.error || (!res.pcName && ips.length === 0)) {
        toast.warning(`Could not resolve "${host}". Check the name/IP and try again.`);
        return;
      }
      setForm((prev) => ({
        ...prev,
        pcName: (res.pcName || (isIpv4(host) ? prev.pcName : host)).trim() || prev.pcName,
        networkIp: (isIpv4(host) ? host : ips[0]) || prev.networkIp,
      }));
      toast.success(`Updated host details for ${res.pcName || host}`);
    } finally {
      setResolvingHost(false);
    }
  };

  const addDetected = async (printer: DetectedPrinter) => {
    // Resolve this PC's identity fresh: `localHostname` stays empty until the
    // mount effect's agent probe resolves, so a printer added in that window
    // would otherwise be mirrored to the registry with no host and become
    // impossible to add from another PC.
    const identity = await getPrintAgentIdentities(true);
    const localName = (localHostname || identity.hostname || '').trim();
    const { shareName, remoteHost } = resolveDetectedPrinter(printer, localName);
    if (!shareName) {
      toast.warning(`"${printer.name}" is not shared. Share it in Windows, then re-detect.`);
      return;
    }

    // A printer installed from another PC exposes that PC (and its share) in the
    // port; a locally shared printer belongs to this PC. Either way, resolve the
    // host through the agent so BOTH the PC name and the network IP are filled.
    const targetHost = (remoteHost || localName).trim();
    const { pcName, networkIp } = await resolveHostFields(targetHost, localName);
    const device = makeDevice({
      name: printer.name || shareName,
      type: 'printer',
      shareName,
      pcName,
      networkIp,
      workstation: targetHost,
      enabled: true,
      isReceiptPrinter: false,
    });
    persist([...devices, device]);
    syncDevice(device);
    toast.success(
      targetHost ? `Added "${device.name}" on ${targetHost}` : `Added "${device.name}"`,
    );
  };

  /** Open the "share this printer" dialog (enables the Windows share via the agent). */
  const openShare = (printer: DetectedPrinter) => {
    setShareTarget(printer);
    setShareNameInput(suggestShareName(printer.name));
  };

  const handleShare = async () => {
    if (!shareTarget) return;
    const shareName = shareNameInput.trim();
    if (!shareName) {
      toast.error('Enter a share name');
      return;
    }
    setSharing(true);
    try {
      await agentSharePrinter(shareTarget.name, shareName);
      toast.success(`Shared "${shareTarget.name}" as ${shareName}`);
      setShareTarget(null);
      // Refresh detection so the printer now shows its share name.
      await handleDetect();
    } catch (err: any) {
      toast.error(err?.message || 'Could not share the printer');
    } finally {
      setSharing(false);
    }
  };

  // ── Printers registered by other PCs (global registry) ────────────────────

  const openRegistry = async () => {
    setRegistryOpen(true);
    setLoadingRegistry(true);
    try {
      const res = await settingsService.listDevices();
      // Only printers with a share name can be targeted; skip this PC's own
      // entries (already configured locally) and non-printer devices.
      const rows = (res.data || []).filter(
        (d) =>
          d.shareName &&
          ['thermal_printer', 'label_printer', 'printer'].includes(d.type) &&
          !(d.workstation && localHostname && d.workstation === localHostname) &&
          !devices.some((local) => local.id === d.clientId),
      );
      setRegistry(rows);
    } catch (err: any) {
      toast.error(err?.message || 'Could not load the shared printer registry');
      setRegistry([]);
    } finally {
      setLoadingRegistry(false);
    }
  };

  /** Host to target a registry printer on: explicit host, else the registering PC. */
  const registryHost = (d: DevicePayload): string =>
    (d.networkIp || d.pcName || d.workstation || '').trim();

  const addFromRegistry = (entry: DevicePayload) => {
    const host = registryHost(entry);
    if (!entry.shareName || !host) {
      toast.warning(
        `"${entry.name}" has no host recorded. Add it manually with its PC name or IP.`,
      );
      return;
    }
    // Reuse the registry clientId so we upsert the same row instead of creating
    // a duplicate in the global registry.
    const device = makeDevice({
      id: entry.clientId || undefined,
      name: entry.name || entry.shareName,
      type: (entry.type as DeviceType) || 'printer',
      shareName: entry.shareName,
      pcName: entry.pcName || entry.workstation || '',
      networkIp: entry.networkIp || '',
      workstation: entry.workstation || '',
      enabled: true,
      isReceiptPrinter: false,
      isLabelPrinter: false,
    });
    persist([...devices, device]);
    syncDevice(device);
    setRegistryOpen(false);
    toast.success(`Added "${device.name}" from ${host}`);
  };

  const deviceToDelete = deleteId ? devices.find((d) => d.id === deleteId) : null;

  // Which printer each automatic (agent) print job resolves to on this PC.
  const receiptPrinter = devices.find((d) => d.isReceiptPrinter && d.enabled);
  const labelPrinter = devices.find((d) => d.isLabelPrinter && d.enabled);
  const diagnosticReceiptPrinter = devices.find((d) => d.isDiagnosticReceiptPrinter && d.enabled);
  const invoicePrinter = devices.find((d) => d.isInvoicePrinter && d.enabled);
  const documentPrinter = devices.find((d) => d.isDocumentPrinter && d.enabled);

  // Page tabs kept in the URL: ?tab=devices | routing | agents | urls | logs
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabParam = searchParams.get('tab');
  const activeTab: 'devices' | 'routing' | 'agents' | 'urls' | 'logs' =
    activeTabParam === 'routing' ||
    activeTabParam === 'agents' ||
    activeTabParam === 'urls' ||
    activeTabParam === 'logs'
      ? activeTabParam
      : 'devices';
  const setTab = (tab: 'devices' | 'routing' | 'agents' | 'urls' | 'logs') => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  // Load the print activity log for a given page (defaults to page 1).
  const loadPrintLogs = async (page = 1, limit = logsPageSize) => {
    setLogsLoading(true);
    setLogsError(null);
    // Selections are page-scoped, so drop them whenever the page reloads.
    setSelectedLogIds(new Set());
    try {
      const res = await settingsService.listPrintJobs({
        page,
        limit,
        status: logStatus || undefined,
        source: logSource || undefined,
        search: logSearch.trim() || undefined,
      });
      setPrintLogs(res.data || []);
      setLogsPage(page);
      setLogsTotal(res.meta?.total ?? res.data?.length ?? 0);
      setLogsTotalPages(res.meta?.totalPages ?? 1);
    } catch (err: any) {
      setLogsError(err?.message || 'Could not load print logs');
      setPrintLogs([]);
      setLogsTotal(0);
      setLogsTotalPages(1);
    } finally {
      setLogsLoading(false);
    }
  };

  // Fetch logs when the tab opens and whenever a filter or page size changes
  // (debounced). Changing the page size must reset back to the first page.
  useEffect(() => {
    if (activeTab !== 'logs') return;
    const timer = setTimeout(() => {
      void loadPrintLogs(1);
    }, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, logStatus, logSource, logSearch, logsPageSize]);

  // Load the auto-delete retention setting when the tab opens.
  useEffect(() => {
    if (activeTab !== 'logs') return;
    let cancelled = false;
    settingsService
      .getPrintLogSettings()
      .then((res) => {
        if (cancelled) return;
        const days = res.data?.retentionDays ?? 0;
        setRetentionDays(days);
        setRetentionDraft(days);
      })
      .catch(() => {
        /* settings unavailable — leave the defaults */
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  const selectedLogCount = selectedLogIds.size;
  const allVisibleSelected =
    printLogs.length > 0 && printLogs.every((log) => selectedLogIds.has(log.id));

  const toggleLogSelected = (id: string) => {
    setSelectedLogIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedLogIds((prev) => {
      if (printLogs.length > 0 && printLogs.every((log) => prev.has(log.id))) {
        return new Set();
      }
      return new Set(printLogs.map((log) => log.id));
    });
  };

  const saveRetention = async () => {
    setSavingRetention(true);
    try {
      const res = await settingsService.updatePrintLogSettings(retentionDraft);
      const days = res.data?.retentionDays ?? retentionDraft;
      setRetentionDays(days);
      setRetentionDraft(days);
      toast.success(
        days > 0
          ? `Logs older than ${days} day(s) will be deleted automatically.`
          : 'Automatic log deletion is off.',
      );
    } catch (err: any) {
      toast.error(err?.message || 'Could not save the retention setting');
    } finally {
      setSavingRetention(false);
    }
  };

  const handleBulkDeleteLogs = async () => {
    const ids = Array.from(selectedLogIds);
    if (ids.length === 0) {
      toast.error('Select at least one log to delete.');
      return;
    }
    setDeletingLogs(true);
    try {
      const res = await settingsService.bulkDeletePrintJobs(ids);
      toast.success(`Deleted ${res.data?.deleted ?? ids.length} log(s)`);
      setBulkDeleteOpen(false);
      await loadPrintLogs(logsPage);
    } catch (err: any) {
      toast.error(err?.message || 'Could not delete the selected logs');
    } finally {
      setDeletingLogs(false);
    }
  };

  const handleClearLogs = async () => {
    setDeletingLogs(true);
    try {
      const res = await settingsService.clearPrintJobs();
      toast.success(`Cleared ${res.data?.deleted ?? 0} log(s)`);
      setClearLogsOpen(false);
      await loadPrintLogs(1);
    } catch (err: any) {
      toast.error(err?.message || 'Could not clear the print log');
    } finally {
      setDeletingLogs(false);
    }
  };

  const handleReprintLog = async (id: string) => {
    setReprintingId(id);
    try {
      const res = await settingsService.reprintPrintJob(id);
      if (res.data?.delivered) {
        toast.success('Print job re-sent to the printer');
      } else {
        toast.warning('Re-queued, but the printer PC is not connected right now.');
      }
      await loadPrintLogs(logsPage);
    } catch (err: any) {
      toast.error(err?.message || 'Could not reprint this job');
    } finally {
      setReprintingId(null);
    }
  };

  /** Page numbers (with ellipses) for the log pagination control. */
  const logPageItems = (): Array<number | '…'> => {
    const total = logsTotalPages;
    const current = logsPage;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const items: Array<number | '…'> = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    if (start > 2) items.push('…');
    for (let p = start; p <= end; p++) items.push(p);
    if (end < total - 1) items.push('…');
    items.push(total);
    return items;
  };

  // All print job types, shown together in a single table on the routing tab.
  // `flag` is the device boolean a role sets; `types` are the device types that
  // may be assigned to it. Roles are assigned from the table's Assign button.
  const routingRoles = [
    {
      id: 'receipts',
      label: 'Pharmacy receipts',
      icon: <Printer size={16} className="text-primary" />,
      device: receiptPrinter,
      flag: 'isReceiptPrinter' as const,
      types: ['thermal_printer'] as DeviceType[],
      tone: 'warning' as const,
      method: 'Raw ESC/POS thermal printer',
      hint: 'Thermal receipt printer driven with raw ESC/POS (no browser dialog).',
    },
    {
      id: 'labels',
      label: 'Specimen labels',
      icon: <Tags size={16} className="text-primary" />,
      device: labelPrinter,
      flag: 'isLabelPrinter' as const,
      types: PRINTER_DEVICE_TYPES,
      tone: 'warning' as const,
      method: 'ESC/POS or PDF (regular printer)',
      hint: 'Printer for specimen / collection labels.',
    },
    {
      id: 'diagnostic-receipts',
      label: 'Diagnostic receipts',
      icon: <ClipboardList size={16} className="text-primary" />,
      device: diagnosticReceiptPrinter,
      flag: 'isDiagnosticReceiptPrinter' as const,
      types: PRINTER_DEVICE_TYPES,
      tone: 'muted' as const,
      method: 'PDF via the local print agent',
      hint: 'Lab test receipts printed from the OPD → Diagnostic Receipts pages.',
    },
    {
      id: 'invoices',
      label: 'Invoices',
      icon: <Receipt size={16} className="text-primary" />,
      device: invoicePrinter,
      flag: 'isInvoicePrinter' as const,
      types: PRINTER_DEVICE_TYPES,
      tone: 'muted' as const,
      method: 'PDF via the local print agent',
      hint: 'Patient invoices printed from Billing → Invoices.',
    },
    {
      id: 'documents',
      label: 'Documents',
      icon: <Monitor size={16} className="text-primary" />,
      device: documentPrinter,
      flag: 'isDocumentPrinter' as const,
      types: PRINTER_DEVICE_TYPES,
      tone: 'muted' as const,
      method: 'PDF via the browser dialog',
      hint: 'Other A4 / PDF prints such as reports and statements.',
    },
  ];

  type RoutingRole = (typeof routingRoles)[number];

  // ── Role assignment (the Assign button in the routing table) ──────────────
  const [assignRoleId, setAssignRoleId] = useState<string | null>(null);
  const assignRole = assignRoleId ? routingRoles.find((r) => r.id === assignRoleId) : undefined;

  /** Devices that may be assigned to a role (right type, enabled, has a share). */
  const roleCandidates = (role: RoutingRole): ConnectedDevice[] =>
    devices.filter((d) => d.enabled && d.shareName && role.types.includes(d.type));

  const openAssign = (role: RoutingRole) => {
    // No suitable device yet — go straight to adding one.
    if (roleCandidates(role).length === 0) {
      openAdd();
      return;
    }
    setAssignRoleId(role.id);
  };

  const assignRoleToDevice = (role: RoutingRole, device: ConnectedDevice) => {
    const next = devices.map((d) =>
      (d.id === device.id
        ? { ...d, [role.flag]: true }
        : { ...d, [role.flag]: false }) as ConnectedDevice,
    );
    const before = new Map(devices.map((d) => [d.id, d]));
    persist(next);
    // Keep the global registry in sync for every device whose role changed.
    next.forEach((d) => {
      if ((before.get(d.id) as any)?.[role.flag] !== d[role.flag]) syncDevice(d);
    });
    setAssignRoleId(null);
    toast.success(`"${device.name || device.shareName}" assigned to ${role.label}`);
  };

  // Revoked agents stay in the backend (so the tokens remain disabled) but are
  // hidden from the list by default. The toggle reveals them for auditing.
  const revokedAgents = printAgents.filter((agent) => !agent.enabled);
  const visiblePrintAgents = showRevokedAgents ? printAgents : printAgents.filter((a) => a.enabled);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <Monitor className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text">Devices</h1>
            <p className="text-sm text-text-muted">
              Connect and manage printers, scanners and other hardware attached to this computer
            </p>
          </div>
        </div>
        <Button onClick={openAdd}>
          <Plus size={16} className="mr-2" />
          Add Device
        </Button>
      </div>

      {/* Tabs (active tab kept in the URL): devices | print routing | agents | allowed URLs */}
      <div className="flex w-fit items-center gap-1 rounded-lg border border-border bg-background p-1">
        <button
          type="button"
          onClick={() => setTab('devices')}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            activeTab === 'devices'
              ? 'bg-primary text-white'
              : 'text-text-muted hover:bg-surface hover:text-text'
          }`}
        >
          <Monitor size={15} />
          Devices
        </button>
        <button
          type="button"
          onClick={() => setTab('routing')}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            activeTab === 'routing'
              ? 'bg-primary text-white'
              : 'text-text-muted hover:bg-surface hover:text-text'
          }`}
        >
          <Printer size={15} />
          Print routing
        </button>
        <button
          type="button"
          onClick={() => setTab('agents')}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            activeTab === 'agents'
              ? 'bg-primary text-white'
              : 'text-text-muted hover:bg-surface hover:text-text'
          }`}
        >
          <Server size={15} />
          Agents
        </button>
        <button
          type="button"
          onClick={() => setTab('urls')}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            activeTab === 'urls'
              ? 'bg-primary text-white'
              : 'text-text-muted hover:bg-surface hover:text-text'
          }`}
        >
          <Link2 size={15} />
          URLs
        </button>
        <button
          type="button"
          onClick={() => setTab('logs')}
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            activeTab === 'logs'
              ? 'bg-primary text-white'
              : 'text-text-muted hover:bg-surface hover:text-text'
          }`}
        >
          <ScrollText size={15} />
          Print log
        </button>
      </div>

      {/* One-time hint when the browser can't reach the local print agent. The
          usual cause on a public HTTPS dashboard is Chrome's Local Network
          Access gate, which is a browser permission — not something the agent
          or its allow-list can fix. */}
      {agentStatus.unreachable && !agentHintDismissed && (
        <div className="flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/10 p-4">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-warning" />
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-medium text-warning">
              Can&apos;t reach the local print agent
            </p>
            <p className="text-xs text-text-muted">
              This dashboard could not reach the print agent at{' '}
              <span className="font-mono">{agentStatus.url}</span>. If the agent is
              installed and running, your browser is likely blocking local network
              access. In Chrome, open the site controls (the lock icon in the address
              bar) and set <span className="font-medium">Local Network Access</span> —
              or <span className="font-medium">Apps on device</span> in newer versions —
              to <span className="font-medium">Allow</span>, then retry. Printing may
              still work: jobs are delivered through the agent&apos;s socket connection,
              independent of this check.
            </p>
            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => void retryPrintAgent()}
                disabled={agentRetrying}
              >
                {agentRetrying ? (
                  <Loader2 size={14} className="mr-1 animate-spin" />
                ) : (
                  <RefreshCw size={14} className="mr-1" />
                )}
                Retry
              </Button>
              <Button size="sm" variant="ghost" onClick={dismissAgentHint}>
                Dismiss
              </Button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'routing' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Printer size={18} className="text-primary" />
              Print routing
            </CardTitle>
            <CardDescription>Which printer this computer uses for each print job.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-background text-left text-xs uppercase tracking-wide text-text-muted">
                    <th className="px-4 py-2 font-semibold">Job</th>
                    <th className="px-4 py-2 font-semibold">Printer</th>
                    <th className="px-4 py-2 font-semibold">Location</th>
                    <th className="px-4 py-2 font-semibold">Method</th>
                    <th className="px-4 py-2 font-semibold">Status</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {routingRoles.map((role) => {
                    const host = role.device
                      ? getDeviceHost(role.device) || 'localhost'
                      : '';
                    return (
                      <tr key={role.id} className="border-b border-border last:border-0">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {role.icon}
                            <div className="min-w-0">
                              <p className="font-medium text-text">{role.label}</p>
                              <p className="text-xs text-text-muted">{role.hint}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-text">
                          {role.device?.name || <span className="text-text-muted">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          {role.device?.shareName ? (
                            <code className="bg-background px-1 rounded text-xs">
                              \\{host}\{role.device.shareName}
                            </code>
                          ) : (
                            <span className="text-text-muted">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-text-muted">{role.method}</td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={
                              role.device ? 'primary' : role.tone === 'warning' ? 'warning' : 'default'
                            }
                          >
                            {role.device
                              ? 'In use'
                              : role.tone === 'warning'
                                ? 'Not set'
                                : 'Browser dialog'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAssign(role)}
                          >
                            {role.device ? (
                              <Pencil size={14} className="mr-1" />
                            ) : (
                              <Plus size={14} className="mr-1" />
                            )}
                            {role.device ? 'Change' : 'Assign'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {(!receiptPrinter || !labelPrinter) && (
              <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg text-sm text-warning">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>
                  {!receiptPrinter && !labelPrinter
                    ? 'No receipt or label printer is set. Automatic receipt/label printing will fall back to the default printer configured on the server.'
                    : `No ${!receiptPrinter ? 'receipt' : 'label'} printer is set. Those jobs will fall back to another printer.`}
                </span>
              </div>
            )}
            <p className="text-xs text-text-muted">
              Invoices use the Invoices printer when assigned, otherwise the Documents printer.
              Other documents (reports, statements) open the browser print dialog unless a Documents
              printer is assigned above.
            </p>
          </CardContent>
        </Card>
      )}

      {activeTab === 'devices' && (
        <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Device list */}
        <div className="lg:col-span-2 space-y-4">
          {devices.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center justify-center py-12 text-center gap-2">
                  <Monitor className="w-10 h-10 text-text-muted" />
                  <p className="text-sm text-text-muted">No devices configured yet</p>
                  <Button onClick={openAdd} variant="outline" size="sm">
                    <Plus size={14} className="mr-1" />
                    Add your first device
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            devices.map((device) => {
              const meta = TYPE_META[device.type];
              const Icon = meta.icon;
              return (
                <Card key={device.id} className={device.enabled ? '' : 'opacity-60'}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-text truncate">{device.name}</h3>
                          {device.isReceiptPrinter && (
                            <Badge variant="primary">Receipt Printer</Badge>
                          )}
                          {device.isLabelPrinter && (
                            <Badge variant="primary">Collection Label Printer</Badge>
                          )}
                          {device.isDiagnosticReceiptPrinter && (
                            <Badge variant="primary">Diagnostic Receipts</Badge>
                          )}
                          {device.isInvoicePrinter && <Badge variant="primary">Invoices</Badge>}
                          {device.isDocumentPrinter && <Badge variant="primary">Documents</Badge>}
                          {!device.enabled && <Badge variant="default">Disabled</Badge>}
                        </div>
                        <p className="text-sm text-text-muted mt-0.5">{meta.label}</p>
                        {meta.usesShare && (
                          <p className="text-xs text-text-muted mt-1">
                            Share: <code className="bg-background px-1 rounded">{device.shareName || '—'}</code>
                          </p>
                        )}
                        {meta.usesPort && (
                          <p className="text-xs text-text-muted mt-1">
                            Port: <code className="bg-background px-1 rounded">{device.port || '—'}</code>
                          </p>
                        )}
                        {(device.pcName || device.networkIp) && (
                          <p className="text-xs text-text-muted mt-1">
                            Host:{' '}
                            <code className="bg-background px-1 rounded">
                              {[device.pcName, device.networkIp].filter(Boolean).join(' · ') || '—'}
                            </code>
                          </p>
                        )}
                        {meta.usesShare && device.shareName && (
                          <div className="mt-1">
                            {!getDeviceHost(device) ? (
                              <span
                                className="text-xs text-warning inline-flex items-center gap-1"
                                title="Set this printer's PC name or network IP (Edit) so the job can be delivered to its print agent."
                              >
                                <AlertCircle size={12} /> Host not set — edit this device
                              </span>
                            ) : isRemoteHost(getDeviceHost(device), localHostname, localIps) ? (
                              <span className="text-xs inline-flex items-center gap-2">
                                <span
                                  className="text-primary inline-flex items-center gap-1"
                                  title={`This printer is on ${getDeviceHost(device)}; receipts/labels print there through the print agent (cloud relay).`}
                                >
                                  <Cloud size={12} /> Prints via agent on {getDeviceHost(device)}
                                </span>
                                {isHostOnline(getDeviceHost(device), device.shareName) ? (
                                  <span
                                    className="text-success inline-flex items-center gap-1"
                                    title="A print agent on the printer's PC is connected and will receive jobs."
                                  >
                                    <CheckCircle2 size={12} /> Agent online
                                  </span>
                                ) : (
                                  <span
                                    className="text-danger inline-flex items-center gap-1"
                                    title="No print agent is connected for this PC — jobs are queued and delivered when it reconnects."
                                  >
                                    <AlertCircle size={12} /> Agent offline
                                  </span>
                                )}
                              </span>
                            ) : (
                              renderReachability(device)
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => handleToggleEnabled(device)}
                          className={`p-2 rounded-lg transition-colors ${
                            device.enabled
                              ? 'text-success hover:bg-success/10'
                              : 'text-text-muted hover:bg-background'
                          }`}
                          title={device.enabled ? 'Disable' : 'Enable'}
                        >
                          <CheckCircle2 size={16} />
                        </button>
                        <button
                          onClick={() => openEdit(device)}
                          className="p-2 rounded-lg text-text-muted hover:bg-background transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteId(device.id)}
                          className="p-2 rounded-lg text-danger hover:bg-danger/10 transition-colors"
                          title="Remove"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Detect printers */}
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search size={18} className="text-primary" />
              Detect Printers
            </CardTitle>
            <CardDescription>
              Scan this computer for shared printers, then add them as devices. Scanners and other
              USB devices are added manually.
            </CardDescription>
            <p className="text-xs text-text-muted pt-1">
              Detection runs on this PC via the local print agent. If no printers appear, install
              the agent (see <code>print-agent/README.md</code>) or add a device manually.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button onClick={handleDetect} disabled={detecting} variant="outline" className="w-full">
              {detecting ? (
                <Loader2 size={16} className="mr-2 animate-spin" />
              ) : (
                <RefreshCw size={16} className="mr-2" />
              )}
              Detect Printers
            </Button>

            <Button
              onClick={openRegistry}
              variant="outline"
              className="w-full"
              title="Pick a printer that another PC has already registered and shared"
            >
              <Monitor size={16} className="mr-2" />
              Printers on other PCs
            </Button>

            {detecting ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : printers === null ? (
              <p className="text-xs text-text-muted text-center py-4">
                Click "Detect Printers" to scan this computer
              </p>
            ) : printers.length === 0 ? (
              <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg text-sm text-warning">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>No printers found. Add a device manually instead.</span>
              </div>
            ) : (
              <div className="space-y-2">
                {printers.map((printer) => {
                  const { shareName, remoteHost } = resolveDetectedPrinter(printer, localHostname);
                  return (
                    <div
                      key={printer.name || shareName || printer.portName}
                      className={`p-3 border rounded-lg ${
                        shareName ? 'border-border bg-background' : 'border-border bg-background opacity-60'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-text truncate">{printer.name}</p>
                          <p className="text-xs text-text-muted truncate">
                            {shareName ? (
                              <>
                                Shared as <strong>{shareName}</strong>
                                {remoteHost ? (
                                  <>
                                    {' '}on <strong>{remoteHost}</strong>
                                  </>
                                ) : null}
                              </>
                            ) : (
                              'Not shared'
                            )}
                          </p>
                        </div>
                        {shareName ? (
                          <Button size="sm" variant="outline" onClick={() => addDetected(printer)}>
                            <Plus size={14} className="mr-1" />
                            Add
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => openShare(printer)}>
                            <Plug size={14} className="mr-1" />
                            Share
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

        </>
      )}

      {activeTab === 'agents' && (
        <Card>
          <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
            <div className="space-y-1.5">
              <CardTitle className="flex items-center gap-2">
                <Server size={18} className="text-primary" />
                Print agents
              </CardTitle>
              <CardDescription>
                Computers running the Fast Aid Print Agent that receive real-time print jobs. An
                agent enrolls automatically; revoke one to disable its access (it must enroll again).
              </CardDescription>
            </div>
            {revokedAgents.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                onClick={() => setShowRevokedAgents((v) => !v)}
                title={showRevokedAgents ? 'Hide revoked agents' : 'Show revoked agents'}
              >
                {showRevokedAgents ? (
                  <EyeOff size={14} className="mr-1" />
                ) : (
                  <Eye size={14} className="mr-1" />
                )}
                {showRevokedAgents ? 'Hide revoked' : `Show revoked (${revokedAgents.length})`}
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-2">
            {visiblePrintAgents.length === 0 ? (
              <p className="text-sm text-text-muted py-4 text-center">
                No print agents have enrolled yet. Install the agent on each printer PC and set its
                backend URL and enrollment key.
              </p>
            ) : (
              visiblePrintAgents.map((agent) => (
                <div
                  key={agent.id}
                  className={`flex items-center justify-between gap-3 rounded-lg border border-border bg-background p-3 ${
                    agent.enabled ? '' : 'opacity-60'
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={`inline-block h-2 w-2 rounded-full ${
                          agent.online ? 'bg-success' : 'bg-text-muted/50'
                        }`}
                        title={agent.online ? 'Online' : 'Offline'}
                      />
                      <p className="text-sm font-medium text-text truncate">
                        {agent.hostname || agent.id}
                      </p>
                      {!agent.enabled && <Badge variant="default">Revoked</Badge>}
                      {agent.agentVersion && (
                        <span className="text-xs text-text-muted">v{agent.agentVersion}</span>
                      )}
                    </div>
                    <p className="text-xs text-text-muted mt-0.5 truncate">
                      {(agent.printers || []).length} printer(s)
                      {agent.lastSeenAt
                        ? ` · last seen ${new Date(agent.lastSeenAt).toLocaleString()}`
                        : ''}
                    </p>
                  </div>
                  {agent.enabled ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => revokeAgent(agent)}
                      title="Revoke this agent's access"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Revoke
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-danger border-danger/40 hover:bg-danger/10"
                      onClick={() => setRemoveAgentTarget(agent)}
                      title="Permanently remove this revoked agent (it can enrol again)"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Remove
                    </Button>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'urls' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 size={18} className="text-primary" />
              Allowed dashboard URLs
            </CardTitle>
            <CardDescription>
              The dashboard URLs each computer allows on its local print agent. Save here and every
              open dashboard pushes the list to its own PC's agent automatically — no need to edit
              the agent on each machine. The address you're using right now is always kept.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <textarea
              value={originsText}
              onChange={(e) => setOriginsText(e.target.value)}
              rows={4}
              spellCheck={false}
              placeholder={'https://admin.fastaid.com.bd\nhttp://localhost:5173'}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <p className="text-xs text-text-muted">
              One URL per line. Each must match the address staff open the dashboard from (scheme +
              host). A print agent rejects requests from any origin not listed here.
            </p>
            <div className="flex items-center justify-end">
              <Button onClick={handleSaveOrigins} disabled={savingOrigins || !originsLoaded}>
                {savingOrigins ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <Save size={16} className="mr-2" />
                )}
                Save allowed URLs
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'logs' && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <ScrollText size={18} className="text-primary" />
                  Print log
                </CardTitle>
                <CardDescription>
                  Every print job recorded by this server, newest first. Use it to trace missing,
                  queued or failed prints.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void loadPrintLogs(logsPage)}
                  disabled={logsLoading}
                >
                  {logsLoading ? (
                    <Loader2 size={14} className="mr-1 animate-spin" />
                  ) : (
                    <RefreshCw size={14} className="mr-1" />
                  )}
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-danger hover:bg-danger/10"
                  onClick={() => setBulkDeleteOpen(true)}
                  disabled={selectedLogCount === 0 || deletingLogs}
                  title="Delete the selected log rows"
                >
                  <Trash2 size={14} className="mr-1" />
                  Delete selected{selectedLogCount > 0 ? ` (${selectedLogCount})` : ''}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-danger hover:bg-danger/10"
                  onClick={() => setClearLogsOpen(true)}
                  disabled={logsTotal === 0 || deletingLogs}
                  title="Delete every print log"
                >
                  <Trash2 size={14} className="mr-1" />
                  Clear log
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Auto-delete retention */}
            <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-background p-3">
              <div className="min-w-[220px]">
                <label className="mb-1 block text-xs font-medium text-text-muted">
                  Auto-delete old logs
                </label>
                <select
                  value={retentionDraft}
                  onChange={(e) => setRetentionDraft(Number(e.target.value))}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value={0}>Never (keep forever)</option>
                  <option value={7}>Older than 7 days</option>
                  <option value={30}>Older than 30 days</option>
                  <option value={90}>Older than 90 days</option>
                  <option value={180}>Older than 180 days</option>
                  <option value={365}>Older than 1 year</option>
                </select>
              </div>
              <Button
                size="sm"
                onClick={() => void saveRetention()}
                disabled={savingRetention || retentionDraft === retentionDays}
              >
                {savingRetention ? (
                  <Loader2 size={14} className="mr-1 animate-spin" />
                ) : (
                  <Save size={14} className="mr-1" />
                )}
                Save
              </Button>
              <p className="flex-1 text-xs text-text-muted">
                Runs in the background and removes finished/failed logs past the retention. Queued
                jobs are never removed.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[220px] flex-1">
                <Search
                  size={15}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
                />
                <Input
                  value={logSearch}
                  onChange={(e) => setLogSearch(e.target.value)}
                  placeholder="Search printer, host, source or requester…"
                  className="pl-9"
                />
              </div>
              <select
                value={logStatus}
                onChange={(e) => setLogStatus(e.target.value)}
                className="rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">All statuses</option>
                <option value="pending">Pending</option>
                <option value="delivered">Delivered</option>
                <option value="printing">Printing</option>
                <option value="done">Done</option>
                <option value="failed">Failed</option>
                <option value="claimed">Claimed</option>
              </select>
              <Input
                value={logSource}
                onChange={(e) => setLogSource(e.target.value)}
                placeholder="Source (e.g. pharmacy.sale.receipt)"
                className="w-[240px]"
              />
            </div>

            {logsError ? (
              <div className="flex items-start gap-2 p-3 bg-danger/10 border border-danger/20 rounded-lg text-sm text-danger">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <span>{logsError}</span>
              </div>
            ) : logsLoading && printLogs.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : printLogs.length === 0 ? (
              <p className="py-10 text-center text-sm text-text-muted">
                No print logs match these filters yet.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-background text-left text-xs uppercase tracking-wide text-text-muted">
                      <th className="w-10 px-4 py-2">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleSelectAllVisible}
                          aria-label="Select all visible logs"
                          className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                        />
                      </th>
                      <th className="px-4 py-2 font-semibold">When</th>
                      <th className="px-4 py-2 font-semibold">Status</th>
                      <th className="px-4 py-2 font-semibold">Source</th>
                      <th className="px-4 py-2 font-semibold">Printer</th>
                      <th className="px-4 py-2 font-semibold">Type</th>
                      <th className="px-4 py-2 font-semibold">Size</th>
                      <th className="px-4 py-2 font-semibold">Tries</th>
                      <th className="px-4 py-2 font-semibold">Detail</th>
                      <th className="px-4 py-2 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {printLogs.map((log) => (
                      <tr
                        key={log.id}
                        className={`border-b border-border last:border-0 align-top ${
                          selectedLogIds.has(log.id) ? 'bg-primary/5' : ''
                        }`}
                        title={`Job ${log.id}`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedLogIds.has(log.id)}
                            onChange={() => toggleLogSelected(log.id)}
                            aria-label={`Select log ${log.id}`}
                            className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                          />
                        </td>
                        <td className="px-4 py-3 text-xs text-text-muted whitespace-nowrap">
                          {formatLogTime(log.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={logStatusVariant(log.status)}>{log.status}</Badge>
                        </td>
                        <td className="px-4 py-3 text-xs text-text">
                          {log.source ? (
                            <code className="rounded bg-background px-1">{log.source}</code>
                          ) : (
                            <span className="text-text-muted">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <div className="font-medium text-text">{log.shareName || '—'}</div>
                          <div className="text-text-muted">
                            {log.targetHost
                              ? `\\${log.targetHost}`
                              : log.targetDeviceId
                                ? `agent ${log.targetDeviceId.slice(0, 8)}…`
                                : 'no target'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-text-muted">
                          {log.kind}
                          {log.format ? ` · ${log.format}` : ''}
                        </td>
                        <td className="px-4 py-3 text-xs text-text-muted whitespace-nowrap">
                          {formatBytes(log.payloadBytes)}
                        </td>
                        <td className="px-4 py-3 text-xs text-text-muted">{log.attempts}</td>
                        <td className="px-4 py-3 text-xs">
                          {log.status === 'failed' && log.error ? (
                            <span className="text-danger">{log.error}</span>
                          ) : log.completedAt ? (
                            <span className="text-text-muted">
                              done {formatLogTime(log.completedAt)}
                            </span>
                          ) : log.requestedBy ? (
                            <span className="text-text-muted">by {log.requestedBy}</span>
                          ) : (
                            <span className="text-text-muted">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void handleReprintLog(log.id)}
                            disabled={reprintingId === log.id}
                            title="Re-send this job to its printer"
                          >
                            {reprintingId === log.id ? (
                              <Loader2 size={14} className="mr-1 animate-spin" />
                            ) : (
                              <RefreshCw size={14} className="mr-1" />
                            )}
                            Reprint
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-text-muted">
              <div className="flex items-center gap-3">
                <span>{logsTotal} job(s)</span>
                {selectedLogCount > 0 && (
                  <span className="text-primary">{selectedLogCount} selected</span>
                )}
                <label className="flex items-center gap-1.5">
                  <span>Rows</span>
                  <select
                    value={logsPageSize}
                    onChange={(e) => setLogsPageSize(Number(e.target.value))}
                    className="rounded-lg border border-border bg-surface px-2 py-1 text-xs text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    {[10, 25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={logsPage <= 1 || logsLoading}
                  onClick={() => void loadPrintLogs(1)}
                  title="First page"
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={logsPage <= 1 || logsLoading}
                  onClick={() => void loadPrintLogs(logsPage - 1)}
                >
                  Previous
                </Button>
                {logPageItems().map((item, idx) =>
                  item === '…' ? (
                    <span key={`gap-${idx}`} className="px-1">
                      …
                    </span>
                  ) : (
                    <Button
                      key={item}
                      variant={item === logsPage ? 'primary' : 'outline'}
                      size="sm"
                      className="min-w-[34px]"
                      disabled={logsLoading}
                      onClick={() => void loadPrintLogs(item)}
                    >
                      {item}
                    </Button>
                  ),
                )}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={logsPage >= logsTotalPages || logsLoading}
                  onClick={() => void loadPrintLogs(logsPage + 1)}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={logsPage >= logsTotalPages || logsLoading}
                  onClick={() => void loadPrintLogs(logsTotalPages)}
                  title="Last page"
                >
                  Last
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assign a printer to a routing role */}
      {assignRole && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div>
                <h3 className="text-base font-semibold text-text">Assign printer</h3>
                <p className="text-xs text-text-muted mt-0.5">{assignRole.label}</p>
              </div>
              <button
                onClick={() => setAssignRoleId(null)}
                className="p-1 rounded hover:bg-background text-text-muted"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-2 max-h-[60vh] overflow-y-auto">
              {roleCandidates(assignRole).length === 0 ? (
                <div className="flex flex-col items-start gap-3 p-3 bg-warning/10 border border-warning/20 rounded-lg text-sm text-warning">
                  <span>No printer of the right kind is set up yet.</span>
                  <Button
                    size="sm"
                    onClick={() => {
                      setAssignRoleId(null);
                      openAdd();
                    }}
                  >
                    <Plus size={14} className="mr-1" />
                    Add Device
                  </Button>
                </div>
              ) : (
                roleCandidates(assignRole).map((d) => {
                  const current = assignRole.device?.id === d.id;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => assignRoleToDevice(assignRole, d)}
                      className={`flex w-full items-center justify-between gap-2 rounded-lg border p-3 text-left transition-colors ${
                        current
                          ? 'border-primary bg-primary/5'
                          : 'border-border bg-background hover:bg-surface'
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-text">
                          {d.name || d.shareName}
                        </span>
                        <span className="block truncate text-xs text-text-muted">
                          \\{d.networkIp || d.pcName || 'localhost'}\{d.shareName}
                        </span>
                      </span>
                      {current ? (
                        <Badge variant="primary">Current</Badge>
                      ) : (
                        <span className="shrink-0 text-xs font-medium text-primary">Assign</span>
                      )}
                    </button>
                  );
                })
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
              <button
                type="button"
                onClick={() => {
                  setAssignRoleId(null);
                  openAdd();
                }}
                className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
              >
                + Add new device
              </button>
              <button
                type="button"
                onClick={() => setAssignRoleId(null)}
                className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share a local printer (enables the Windows share via the agent) */}
      {shareTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div>
                <h3 className="text-base font-semibold text-text">Share printer</h3>
                <p className="text-xs text-text-muted mt-0.5">{shareTarget.name}</p>
              </div>
              <button
                onClick={() => setShareTarget(null)}
                className="p-1 rounded hover:bg-background text-text-muted"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-text-muted">
                Runs <code>Set-Printer -Shared</code> on this PC through the print agent so other
                workstations can print to{' '}
                <code>{`\\\\<this-pc>\\${shareNameInput || 'share'}`}</code>. The agent must be running
                as administrator.
              </p>
              <div>
                <label className="text-xs font-medium text-text-muted">Share name</label>
                <Input
                  value={shareNameInput}
                  onChange={(e) => setShareNameInput(e.target.value)}
                  placeholder="RONGTA80"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
              <Button variant="outline" onClick={() => setShareTarget(null)} disabled={sharing}>
                Cancel
              </Button>
              <Button onClick={handleShare} disabled={sharing || !shareNameInput.trim()}>
                {sharing ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
                Share
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit modal */}
      {formOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="text-base font-semibold text-text">
                {editingId ? 'Edit Device' : 'Add Device'}
              </h3>
              <button
                onClick={() => setFormOpen(false)}
                className="p-1 rounded hover:bg-background text-text-muted"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <Input
                label="Device name"
                placeholder="e.g. Front desk receipt printer"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
              />

              <div>
                <label className="mb-1 block text-sm font-medium text-text">Type</label>
                <select
                  value={form.type}
                  onChange={(e) => {
                    const type = e.target.value as DeviceType;
                    setForm((prev) => ({
                      ...prev,
                      type,
                      isReceiptPrinter: type === 'thermal_printer' ? prev.isReceiptPrinter : false,
                      isLabelPrinter: PRINTER_DEVICE_TYPES.includes(type) ? prev.isLabelPrinter : false,
                      isDiagnosticReceiptPrinter: PRINTER_DEVICE_TYPES.includes(type)
                        ? prev.isDiagnosticReceiptPrinter
                        : false,
                      isInvoicePrinter: PRINTER_DEVICE_TYPES.includes(type)
                        ? prev.isInvoicePrinter
                        : false,
                      isDocumentPrinter: PRINTER_DEVICE_TYPES.includes(type)
                        ? prev.isDocumentPrinter
                        : false,
                    }));
                  }}
                  className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {DEVICE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              {TYPE_META[form.type].usesShare && (
                <Input
                  label="Share name"
                  placeholder="e.g. Rongta80"
                  value={form.shareName}
                  onChange={(e) => setField('shareName', e.target.value)}
                />
              )}

              {TYPE_META[form.type].usesPort && (
                <Input
                  label="Port / connection"
                  placeholder="e.g. COM3 or USB"
                  value={form.port}
                  onChange={(e) => setField('port', e.target.value)}
                />
              )}

              <div className="rounded-lg border border-border p-3 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-text-muted">
                    Host (for a shared printer on another PC)
                  </span>
                  <button
                    type="button"
                    onClick={refreshHost}
                    disabled={resolvingHost}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50"
                    title="Re-read the PC name and IP from the local print agent"
                  >
                    {resolvingHost ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <RefreshCw size={12} />
                    )}
                    Refresh
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="PC name"
                    placeholder="e.g. RECEPTION-PC"
                    value={form.pcName}
                    onChange={(e) => setField('pcName', e.target.value)}
                  />
                  <Input
                    label="Network IP"
                    placeholder="e.g. 192.168.1.50"
                    value={form.networkIp}
                    onChange={(e) => setField('networkIp', e.target.value)}
                  />
                </div>
                <p className="text-xs text-text-muted">
                  Set the PC name or network IP so printing targets{' '}
                  <code className="bg-background px-1 rounded">\\host\share</code>. Press{' '}
                  <strong>Refresh</strong> to re-read them from the print agent if the machine name
                  or its IP changed.
                </p>
              </div>

              <p className="text-xs text-text-muted">
                Printer roles (receipts, labels, diagnostic receipts, documents) are assigned from
                the <strong>Print routing</strong> tab.
              </p>

              <label className="flex items-center gap-2 text-sm text-text cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setField('enabled', e.target.checked)}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                Enabled
              </label>
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
              <button
                type="button"
                onClick={() => setFormOpen(false)}
                className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
              >
                Cancel
              </button>
              <Button onClick={handleSaveForm}>
                <Save size={16} className="mr-2" />
                Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Printers registered by other PCs (global registry) */}
      {registryOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div>
                <h3 className="text-base font-semibold text-text">Printers on other PCs</h3>
                <p className="text-xs text-text-muted mt-0.5">
                  Shared printers registered by other workstations. Add one to print to it over the
                  network as <code className="bg-background px-1 rounded">\\PC\share</code>.
                </p>
                <p className="text-xs text-warning mt-1.5">
                  This PC's print agent must list the printer (e.g.{' '}
                  <code className="bg-background px-1 rounded">\\PC\share</code>) under{' '}
                  <strong>Print to another PC</strong> before it can print to it.
                </p>
              </div>
              <button
                onClick={() => setRegistryOpen(false)}
                className="p-1 rounded hover:bg-background text-text-muted"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-4">
              {loadingRegistry ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : !registry || registry.length === 0 ? (
                <div className="flex items-start gap-2 p-3 bg-warning/10 border border-warning/20 rounded-lg text-sm text-warning">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>
                    No printers from other PCs yet. Install the agent and add a printer on the other
                    PC first, then reopen this list.
                  </span>
                </div>
              ) : (
                Object.entries(
                  registry.reduce<Record<string, DevicePayload[]>>((acc, d) => {
                    const host = registryHost(d) || 'Unknown PC';
                    (acc[host] ||= []).push(d);
                    return acc;
                  }, {}),
                ).map(([host, items]) => (
                  <div key={host} className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                      {host}
                    </p>
                    {items.map((entry) => (
                      <div
                        key={entry.clientId || `${host}-${entry.shareName}`}
                        className="flex items-center justify-between gap-3 p-3 border border-border rounded-lg bg-background"
                      >
                        <div className="min-w-0">
                          <p className="flex items-center gap-1.5 text-sm font-medium text-text">
                            <span className="truncate">{entry.name || entry.shareName}</span>
                            {isHostOnline(host, entry.shareName) ? (
                              <span
                                className="inline-flex items-center gap-1 shrink-0 text-xs font-normal text-success"
                                title="A print agent on this PC is connected and will receive jobs."
                              >
                                <CheckCircle2 size={11} /> online
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1 shrink-0 text-xs font-normal text-danger"
                                title="No print agent is connected for this PC. Jobs are queued and delivered when it reconnects."
                              >
                                <AlertCircle size={11} /> offline
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-text-muted truncate">
                            <code className="bg-surface px-1 rounded">{entry.shareName}</code>
                            {' · '}
                            {host}
                          </p>
                        </div>
                        <Button size="sm" variant="outline" onClick={() => addFromRegistry(entry)}>
                          <Plus size={14} className="mr-1" />
                          Add
                        </Button>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>

            <div className="flex items-center justify-end px-5 py-4 border-t border-border">
              <button
                type="button"
                onClick={() => setRegistryOpen(false)}
                className="px-4 py-2 text-sm text-text-muted hover:text-text transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteId}
        title="Remove device?"
        description={
          deviceToDelete ? (
            <>
              <strong>{deviceToDelete.name}</strong> will be removed from this computer.
            </>
          ) : undefined
        }
        confirmLabel="Remove"
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />

      <ConfirmDialog
        open={!!removeAgentTarget}
        title="Remove revoked agent?"
        description={
          removeAgentTarget ? (
            <>
              <strong>{removeAgentTarget.hostname || removeAgentTarget.id}</strong> will be
              permanently removed from the revoked list. If that PC is still running the print
              agent, it can enrol again and will reappear as a new agent.
            </>
          ) : undefined
        }
        confirmLabel="Remove"
        loading={removingAgent}
        onConfirm={removeAgent}
        onCancel={() => setRemoveAgentTarget(null)}
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        title="Delete selected logs?"
        description={`${selectedLogCount} print log(s) will be permanently deleted. This cannot be undone.`}
        confirmLabel="Delete"
        loading={deletingLogs}
        onConfirm={handleBulkDeleteLogs}
        onCancel={() => setBulkDeleteOpen(false)}
      />

      <ConfirmDialog
        open={clearLogsOpen}
        title="Clear the print log?"
        description={`All ${logsTotal} print log(s) will be permanently deleted. This cannot be undone.`}
        confirmLabel="Clear log"
        loading={deletingLogs}
        onConfirm={handleClearLogs}
        onCancel={() => setClearLogsOpen(false)}
      />
    </div>
  );
}
